import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import { CardContent, Grid } from '@mui/material';
import { HttpStatusCode, isAxiosError } from 'axios';
import { formatCurrency } from 'common/constants/currency';
import { Fragment, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { formatDate, formatFirstInstallmentDate } from 'utils/formatDate';
import { useStoreSelector } from 'v3/data/store';
import { ContractingStateEnum } from 'v3/data/store/contracting/enums';
import { showErrorDialog } from 'v3/data/store/error/actions';
import {
  setProposalFinancingDetails,
  setProposalFinancingInformations,
  setProposalProductPrice,
} from 'v3/data/store/proposal/actions';
import { IContractingDetailsParams } from 'v3/pages/Contracting/Details/Cards/types';
import * as api from 'v3/services/api';
import { calculateProductFinalPrice } from 'v3/services/utils/calculateFinalPrice';
import { BodySmallBold } from 'v3/shared/typography/BodySmall';
import { SubtitleLarge } from 'v3/shared/typography/SubtitleLarge';
import { TitleSmall } from 'v3/shared/typography/TitleSmall';
import { theme } from 'v3/styles/global-styles';
import { BodySmallRegularStyled, CardHeaderStyled, CardStyled, ColumnStyled, Content } from './styles';

export const ProposalInformation = () => {
  const { financing, product, financingDetails } = useStoreSelector((store) => store.proposal);
  const { timeRemaining } = useStoreSelector((store) => store.contracting);
  const { contractSignDate } = useStoreSelector((store) => store.contracting.selectedContracting);
  const { contractingState } = useStoreSelector((store) => store.contracting);
  const { contractingKey } = useParams<Readonly<IContractingDetailsParams>>() as IContractingDetailsParams;
  const discount = useStoreSelector((store) => store.proposal.product.discount);
  const insuranceValue = useStoreSelector((store) => store.proposal.financingDetails.insuranceValue);
  const accessoriesValue = useStoreSelector((store) => store.proposal.financingDetails.accessoriesValue);
  const finalPrice = calculateProductFinalPrice(product.price, discount);
  const productSubtotal = finalPrice + insuranceValue + accessoriesValue;

  const getProposalInformation = useCallback(async () => {
    try {
      const responseGetFinancing = await api.getFinancing(contractingKey);
      setProposalFinancingInformations(responseGetFinancing.data);

      const responseGetProduct = await api.getProduct(contractingKey);
      setProposalProductPrice(responseGetProduct.data);

      const responseGetFinancingDetails = await api.getFinancingDetails(contractingKey);
      setProposalFinancingDetails(responseGetFinancingDetails.data);
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === HttpStatusCode.NotFound) console.error(error);
      } else {
        showErrorDialog('Erro ao obter detalhes da proposta', error);
      }
    }
  }, [contractingKey]);

  useEffect(() => {
    getProposalInformation();
  }, [contractingKey]);

  return (
    <CardStyled>
      <CardHeaderStyled
        title={
          <Content>
            <ArticleOutlinedIcon style={{ color: theme.palette.error.main }} />
            <SubtitleLarge>Informações da proposta</SubtitleLarge>
          </Content>
        }
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, sm: 4, md: 4, lg: 2 }}>
            <BodySmallRegularStyled>Crédito aprovado</BodySmallRegularStyled>
            <ColumnStyled>
              <BodySmallBold className="empty-line">
                {financing?.releasedAmount ? formatCurrency(financing.releasedAmount) : '-'}
              </BodySmallBold>
              <TitleSmall style={{ color: theme.palette.secondary.light }}>
                Porcentagem mínima: {financing?.minimumEntryPercentage ? `${financing.minimumEntryPercentage}%` : '-'}
              </TitleSmall>
            </ColumnStyled>
          </Grid>

          <Grid size={{ xs: 6, sm: 4, md: 4, lg: 2 }}>
            <BodySmallRegularStyled>{product.name ? product.name : '-'}</BodySmallRegularStyled>
            <BodySmallBold className="empty-line">
              {product.name ? formatCurrency(product.price - product.discount) : '-'}
            </BodySmallBold>
          </Grid>

          <Grid size={{ xs: 6, sm: 4, md: 4, lg: 2 }}>
            <BodySmallRegularStyled>Seguro</BodySmallRegularStyled>
            <BodySmallBold className="empty-line">
              {financingDetails?.insuranceValue ? financingDetails.insuranceName : '-'}
            </BodySmallBold>
          </Grid>

          <Grid size={{ xs: 6, sm: 4, md: 4, lg: 2 }}>
            <BodySmallRegularStyled>Valor do seguro</BodySmallRegularStyled>
            <BodySmallBold className="empty-line">
              {financingDetails?.insuranceValue ? formatCurrency(financingDetails.insuranceValue) : '-'}
            </BodySmallBold>
          </Grid>

          <Grid size={{ xs: 6, sm: 4, md: 4, lg: 2 }}>
            <BodySmallRegularStyled>Valor dos acessórios</BodySmallRegularStyled>
            <BodySmallBold className="empty-line">
              {financingDetails?.accessoriesValue ? formatCurrency(financingDetails.accessoriesValue) : '-'}
            </BodySmallBold>
          </Grid>

          <Grid size={{ xs: 6, sm: 4, md: 4, lg: 2 }}>
            <BodySmallRegularStyled>Subtotal dos produtos</BodySmallRegularStyled>
            <BodySmallBold className="empty-line">
              {productSubtotal ? formatCurrency(productSubtotal) : '-'}
            </BodySmallBold>
          </Grid>
        </Grid>

        <Grid container spacing={2} style={{ marginTop: '1rem', marginBottom: '1rem' }}>
          <Grid size={{ xs: 6, sm: 4, md: 4, lg: 2 }}>
            <BodySmallRegularStyled>Valor de entrada</BodySmallRegularStyled>
            <BodySmallBold className="empty-line">
              {financing.inputValue ? `${formatCurrency(financing.inputValue)}` : '-'}
            </BodySmallBold>
          </Grid>

          <Grid size={{ xs: 6, sm: 4, md: 4, lg: 2 }}>
            <BodySmallRegularStyled>Parcelas</BodySmallRegularStyled>
            <BodySmallBold className="empty-line">
              {financing?.numberOfInstallments && financing?.installmentValue
                ? `${financing.numberOfInstallments}x de ${formatCurrency(financing.installmentValue)}`
                : '-'}
            </BodySmallBold>
          </Grid>

          <Grid size={{ xs: 6, sm: 4, md: 4, lg: 2 }}>
            <BodySmallRegularStyled>Total(c/juros)</BodySmallRegularStyled>
            <BodySmallBold className="empty-line">
              {financing?.totalFinancingAmount ? `${formatCurrency(financing.totalFinancingAmount)} ` : '-'}
            </BodySmallBold>
          </Grid>
        </Grid>

        <BodySmallBold color={theme.palette.secondary.light}>Detalhes</BodySmallBold>

        <Grid container spacing={2} style={{ marginTop: '0.5rem' }}>
          <Grid size={{ xs: 6, sm: 4, md: 4, lg: 2 }}>
            {contractingState !== ContractingStateEnum.ContractingFinished ? (
              <Fragment>
                <BodySmallRegularStyled>Vencimento da proposta</BodySmallRegularStyled>
                <BodySmallBold className="empty-line">{timeRemaining}</BodySmallBold>
              </Fragment>
            ) : (
              <Fragment>
                <BodySmallRegularStyled>Data do financiamento</BodySmallRegularStyled>
                <BodySmallBold className="empty-line">
                  {contractSignDate ? formatDate(contractSignDate).date : '-'}
                </BodySmallBold>
              </Fragment>
            )}
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 4, lg: 2 }}>
            <BodySmallRegularStyled>Vencimento da 1° parcela</BodySmallRegularStyled>
            <BodySmallBold className="empty-line">
              {financing?.firstInstallmentDate ? formatFirstInstallmentDate(financing.firstInstallmentDate) : '-'}
            </BodySmallBold>
          </Grid>
        </Grid>
      </CardContent>
    </CardStyled>
  );
};
